import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  TablePagination,
} from '@mui/material'
import MainCard from '../../../components/ui/MainCard'
import axiosInstance from '../../../utils/axiosInstance'

interface Order {
  id: string
  jobName: string
  customer: {
    id: string
    name: string
  }
  totalLeads: number
}

interface Pagination {
  page: number
  limit: number
  totalCount: number
  totalPages: number
  hasNext: boolean
  hasPrev: boolean
}

const LeadsOverview = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [orders, setOrders] = useState<Order[]>([])
  const [pagination, setPagination] = useState<Pagination | null>(null)

  useEffect(() => {
    const fetchLeadsOverview = async () => {
      try {
        setLoading(true)
        const response = await axiosInstance.get('/leads-overview', {
          params: {
            page,
            limit: rowsPerPage,
          },
        })
        setOrders(response.data.orders || [])
        setPagination(response.data.pagination)
        setError(false)
      } catch (err) {
        console.error('Error fetching leads overview:', err)
        setError(true)
      } finally {
        setLoading(false)
      }
    }

    fetchLeadsOverview()
  }, [page, rowsPerPage])

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  // Handle row click to view leads for an order
  const handleRowClick = (orderId: string) => {
    navigate(`/leads-by-order/${orderId}`)
  }

  // If loading, show loading indicator
  if (loading) {
    return (
      <MainCard title={intl.formatMessage({ id: 'leads' })}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      </MainCard>
    )
  }

  // If error, show error message
  if (error) {
    return (
      <MainCard title={intl.formatMessage({ id: 'leads' })}>
        <Typography color="error">Error loading leads data</Typography>
      </MainCard>
    )
  }

  return (
    <MainCard title={intl.formatMessage({ id: 'leads' })}>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="leads overview table">
          <TableHead>
            <TableRow>
              <TableCell>Customer</TableCell>
              <TableCell>Job Title</TableCell>
              <TableCell align="right">Total Leads</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => (
              <TableRow
                key={order.id}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
                onClick={() => handleRowClick(order.id)}
              >
                <TableCell component="th" scope="row">
                  {order.customer?.name || 'N/A'}
                </TableCell>
                <TableCell>{order.jobName}</TableCell>
                <TableCell align="right">{order.totalLeads}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={pagination.totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </MainCard>
  )
}

export default LeadsOverview
